import { useEffect, useState } from 'react';
import { Loader2, Save, MessageSquare, RefreshCw } from 'lucide-react';
import { apiFetch } from '../lib/api';
import MarkdownTemplateEditor from '../components/admin/MarkdownTemplateEditor';

interface SystemTemplate {
  templateKey: string;
  name: string;
  content: string;
  description: string;
  variables: string;
  channel: string;
  isActive: number;
}

export default function AdminSystemMessageTemplates() {
  const [templates, setTemplates] = useState<SystemTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    const res = await apiFetch<SystemTemplate[]>('/api/admin/system-message-templates');
    if (res.success && res.data) {
      setTemplates(res.data);
    }
    setLoading(false);
  };

  const handleSave = async (key: string, content: string, isActive: boolean) => {
    setSaving(key);
    const res = await apiFetch(`/api/admin/system-message-templates/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ content, isActive }),
    });
    setSaving(null);
    if (res.success) {
      alert('模版已保存');
      fetchTemplates();
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-7 h-7 text-brand-600" />
          系統訊息模版
        </h1>
        <button
          onClick={fetchTemplates}
          className="p-2 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
          title="刷新"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      <p className="text-sm text-gray-500">
        修改以下模版內容，所有自動發送的 WhatsApp 訊息將即時生效。變量使用 {"{{variable}}"} 格式。
      </p>

      <div className="space-y-6">
        {templates.map((t) => (
          <div key={t.templateKey} className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-gray-900">{t.name}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{t.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                  {t.channel}
                </span>
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={t.isActive === 1}
                    onChange={(e) => {
                      const updated = templates.map((tm) =>
                        tm.templateKey === t.templateKey ? { ...tm, isActive: e.target.checked ? 1 : 0 } : tm
                      );
                      setTemplates(updated);
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  啟用
                </label>
              </div>
            </div>

            <MarkdownTemplateEditor
              value={t.content}
              onChange={(content) => {
                const updated = templates.map((tm) =>
                  tm.templateKey === t.templateKey ? { ...tm, content } : tm
                );
                setTemplates(updated);
              }}
              variables={t.variables.split(',').map((v) => {
                const name = v.trim();
                // 根據變量名稱返回對應的 description 和 example
                const varMap: Record<string, { description: string; example: string }> = {
                  name: { description: '捐款人姓名', example: '陳大文' },
                  amount: { description: '捐款金額', example: '500' },
                  donation_type: { description: '捐款類型', example: '一次性捐款' },
                  donation_num: { description: '捐款編號', example: 'DN6NN1' },
                  quote_content: { description: '阿占金句內容', example: '善良值得被記住' },
                  quote_author: { description: '金句作者', example: '阿占' },
                  phone_case_order_url: { description: '手機殼訂單連結', example: 'https://s2c.jkdcoding.com/phone-case-order?dn=DN6NN1' },
                  card_url: { description: '捐款卡連結', example: 'https://s2c.jkdcoding.com/success?dn=DN6NN1' },
                  monthly_suffix: { description: '月捐後綴', example: '\n\n下月我們會再經 WhatsApp 與您分享這個月的影響力故事。' },
                  meals: { description: '飯餐數量', example: '10' },
                  training_hours: { description: '訓練時數', example: '5' },
                  payment_instructions: { description: '付款指引', example: 'FPS轉數快：108810334' },
                  upload_url: { description: '上傳水單連結', example: 'https://s2c.jkdcoding.com/success?dn=DN6NN1' },
                  receipt_url: { description: '收據申請連結', example: 'https://s2c.jkdcoding.com/receipt-request' },
                  order_num: { description: '訂單編號', example: 'PCMOPPV969' },
                  phone_model: { description: '手機型號', example: 'iPhone 16 Pro Max' },
                  illustration: { description: '插圖選擇', example: '人生大便' },
                  color: { description: '顏色偏好', example: '曼波綠' },
                  sf_locker: { description: '順豐櫃', example: '852H101' },
                  request_num: { description: '收據申請編號', example: 'RCT-TEST-001' },
                };
                return {
                  name,
                  description: varMap[name]?.description || name,
                  example: varMap[name]?.example || '{{' + name + '}}',
                };
              })}
              label="訊息內容"
            />

            <div className="flex justify-end mt-3">
              <button
                onClick={() => handleSave(t.templateKey, t.content, t.isActive === 1)}
                disabled={saving === t.templateKey}
                className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-bold rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {saving === t.templateKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                保存
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
